/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";

// UI components
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
// import GenericAlert from "@ui-components/Alert/GenericAlert";

// Icons
import { ReactComponent as IconEye } from "../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconPlus } from "../../../../shared/assets/Icons/IconPlusNV.svg";
import { ReactComponent as IconEdit } from "../../../../shared/assets/Icons/Icon_Edit.svg";
// import { ReactComponent as IconDelete } from "../../../../shared/assets/Icons/Icon_Delete.svg";

// Data & columns
import { PublicSubnet, columns as baseColumns } from "./SubnetData";

// Example request functions (replace with real ones):
import {
  getAllPublicSubnets,
  addPublicSubnet,
  editPublicSubnet,
} from "./request/request";
import { getIpBlocks } from "../IpBlock/request/request";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import GenericCard from "@ui-components/Card/GenericCard";
import { useAppSelector } from "@redux/hooks";
import { ParsedFilter as OldParsed } from "../../../helpers/advancedFilters";

interface IpBlockItem {
  id: number;
  name: string;
}

function PublicSubnetInventoryPage() {
  // 1) Table data
  const [subnets, setSubnets] = useState<PublicSubnet[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  // 2) Add/Edit modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Fields for Add/Edit
  const [subnetId, setSubnetId] = useState<number>(0);
  const [ip, setIp] = useState<string>("");
  const [mask, setMask] = useState<string>(""); // from your example, mask is a string
  const [status, setStatus] = useState<string>("available"); // or "assigned", etc.
  const [ipBlockId, setIpBlockId] = useState<number>(0);

  // 3) View modal
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedSubnet, setSelectedSubnet] = useState<PublicSubnet | null>(
    null
  );

  // 4) Delete confirmation
  // const [isAlertOpen, setIsAlertOpen] = useState(false);
  // const [subnetToDelete, setSubnetToDelete] = useState<PublicSubnet | null>(
  // 	null
  // );

  // 5) IP Block dropdown states
  const [ipBlockList, setIpBlockList] = useState<IpBlockItem[]>([]);
  const [ipBlockOptions, setIpBlockOptions] = useState<string[]>([]);

  // Auth
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;

  // ------------------------------------------------------------------------------------
  // Fetch all subnets on mount
  // ------------------------------------------------------------------------------------
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;

    (async () => {
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();
        const resp = await getAllPublicSubnets(token);
        // Suppose resp.data is an array of PublicSubnet
        setSubnets(resp.data);
        const ipBlockResp = await getIpBlocks(token);
        // Suppose ipBlockResp.data is an array of { id, name, ... }
        setIpBlockList(ipBlockResp.data);
        setIpBlockOptions(ipBlockResp.data.map((b: IpBlockItem) => b.name));
      } catch (err) {
        console.error("Error fetching public subnets:", err);
      } finally {
        setIsFetching(false);
      }
    })();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  // ------------------------------------------------------------------------------------
  // Add
  // ------------------------------------------------------------------------------------
  function handleAddSubnet() {
    setEditMode(false);
    setIp("");
    setMask("");
    setStatus("available");
    setIpBlockId(0);
    setIsModalOpen(true);
  }

  // ------------------------------------------------------------------------------------
  // Edit
  // ------------------------------------------------------------------------------------
  function handleEditSubnet(row: PublicSubnet) {
    setEditMode(true);
    setSubnetId(row.id);
    setIp(row.ip);
    setMask(row.mask);
    setStatus(row.status);
    setIpBlockId(row.ipBlockId);
    setIsModalOpen(true);
  }

  // ------------------------------------------------------------------------------------
  // Save
  // ------------------------------------------------------------------------------------
  async function handleSaveSubnet() {
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();

      // Build payload from state
      const payload = {
        id: subnetId,
        ip,
        mask,
        status,
        ipBlockId,
      };

      if (editMode) {
        // Update
        await editPublicSubnet(token, subnetId, payload);
        // Update local state
        setSubnets((prev) =>
          prev.map((sub) =>
            sub.id === subnetId ? { ...sub, ...payload } : sub
          )
        );
      } else {
        // Add
        const addResp = await addPublicSubnet(token, payload);
        // If your API returns the newly created object in addResp.data:
        setSubnets((prev) => [...prev, addResp.data]);
      }
    } catch (error) {
      console.error("Error saving subnet:", error);
    } finally {
      setIsFetching(false);
      setIsModalOpen(false);
    }
  }
  // Helper to convert an IP block id => name for the dropdown
  function getIpBlockNameById(id: number) {
    const found = ipBlockList.find((b) => b.id === id);
    return found ? found.name : "";
  }

  // Helper to convert an IP block name => id for the dropdown
  function handleSelectIpBlockName(blockName: string) {
    const found = ipBlockList.find((b) => b.name === blockName);
    setIpBlockId(found ? found.id : 0);
  }

  // ------------------------------------------------------------------------------------
  // View
  // ------------------------------------------------------------------------------------
  function handleViewSubnet(row: PublicSubnet) {
    setSelectedSubnet(row);
    setIsViewModalOpen(true);
  }

  // ------------------------------------------------------------------------------------
  // Delete
  // ------------------------------------------------------------------------------------
  // function handleDeleteSubnet(row: PublicSubnet) {
  // 	setSubnetToDelete(row);
  // 	setIsAlertOpen(true);
  // }

  // async function confirmDeleteSubnet() {
  // 	if (!subnetToDelete) return;
  // 	try {
  // 		setIsFetching(true);
  // 		const token = await getAccessTokenSilently();
  // 		await deletePublicSubnet(token, subnetToDelete.id);

  // 		setSubnets((prev) =>
  // 			prev.filter((item) => item.id !== subnetToDelete.id)
  // 		);
  // 		setSubnetToDelete(null);
  // 		setIsAlertOpen(false);
  // 	} catch (error) {
  // 		console.error("Error deleting subnet:", error);
  // 	} finally {
  // 		setIsFetching(false);
  // 	}
  // }

  // function handleCancelDelete() {
  // 	setSubnetToDelete(null);
  // 	setIsAlertOpen(false);
  // }

  // ------------------------------------------------------------------------------------
  // Override "Actions" column
  // ------------------------------------------------------------------------------------
  const columns = baseColumns.map((col) => {
    if (col.header === "Actions" && col.renderCell) {
      return {
        ...col,
        renderCell: (row: PublicSubnet) => (
          <div className="flex items-center gap-2">
            {/* View */}
            <button
              className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
              onClick={() => handleViewSubnet(row)}
            >
              <IconEye className="w-6 h-6" />
            </button>

            {/* Edit */}
            <button
              className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
              onClick={() => handleEditSubnet(row)}
            >
              <IconEdit className="w-10 h-10" />
            </button>
          </div>
        ),
      };
    }
    return col;
  });

  const suggestedKeys = useMemo(() => {
    const headers = baseColumns
      .map((c) => c.header)
      .filter(Boolean) as string[];

    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }
    return headers;
  }, [baseColumns, isViewingAllCustomers]);

  // ------------------------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------------------------
  return (
    <GenericCard isCollapsible={false}>
      {/* Header */}
      <div className="mb-4 p-5 bg-[var(--semantic-bg-primary)]">
        <div className="flex items-end justify-between">
          <div className="w-[520px] my-5">
            <GenericSearchbar
              placeholder="Find by attribute or value"
              suggestedKeys={suggestedKeys}
              onFiltersChange={setActiveFilters}
            />
          </div>
          <div className="mb-10">
            <GenericButton
              label={
                <div className="flex items-center gap-2">
                  <IconPlus className="w-5 h-5" />
                  <span>Add new Public Subnet</span>
                </div>
              }
              variant="brand"
              size="md"
              onClick={handleAddSubnet}
            />
          </div>
        </div>
      </div>
      <div className="px-5 mb-5">
        {/* Table */}
        <GenericTable
          columns={columns}
          data={subnets}
          initialRowsPerPage={5}
          rowsPerPageOptions={[5, 10]}
          tableClassName="bg-[var(--semantic-bg-primary)]"
          width="100%"
        />
      </div>

      {/* ====================== Modal Add / Edit ====================== */}
      <GenericModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editMode ? "Edit Public Subnet" : "Add Public Subnet"}
        size="md"
      >
        <div className="grid grid-cols-2 gap-4 mb-4">
          <GenericInput label="IP" value={ip} onChange={setIp} />
          <GenericInput label="Mask" value={mask} onChange={setMask} />
          <div className="flex flex-col space-y-2">
            <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
              Status
            </label>
            <GenericDropdown
              items={["available", "assigned", "reserved", "inactive"]} // your two status values
              value={status} // current selection
              onSelect={setStatus} // store in state
              defaultLabel="Pick status..."
              widthClass="w-full"
            />
          </div>
          {/* IP Block => fetch from getIpBlocks => show block.name, store block.id */}
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-semantic-text-secondary">
              IP Block
            </label>
            <GenericDropdown
              items={ipBlockOptions}
              value={ipBlockId ? getIpBlockNameById(ipBlockId) : ""}
              onSelect={handleSelectIpBlockName}
              defaultLabel="Select IP Block..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            className="
              px-4 py-2
              text-sm
              font-medium
              text-semantic-text-secondary
              bg-semantic-bg-primary
              border border-semantic-border-secondary
              rounded
              hover:bg-semantic-state-active
              transition-colors
            "
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </button>
          <button
            className="
              px-4 py-2
              text-sm
              font-medium
              text-white
              bg-semantic-text-brand
              rounded
              hover:bg-opacity-90
              transition-colors
            "
            onClick={handleSaveSubnet}
          >
            Save
          </button>
        </div>
      </GenericModal>

      {/* ====================== Modal View (Read-only) ====================== */}
      <GenericModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="View Public Subnet"
        size="md"
      >
        {selectedSubnet && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <GenericInput
              label="ID"
              value={String(selectedSubnet.id)}
              readOnly
            />
            <GenericInput label="IP" value={selectedSubnet.ip} readOnly />
            <GenericInput label="Mask" value={selectedSubnet.mask} readOnly />
            <GenericInput
              label="Status"
              value={selectedSubnet.status}
              readOnly
            />
            {/* If you want to display the ipBlock name: */}
            <GenericInput
              label="IP Block"
              value={selectedSubnet.ipBlock ? selectedSubnet.ipBlock.name : ""}
              readOnly
            />
          </div>
        )}
        <div className="flex justify-end">
          <button
            className="
              px-4 py-2
              text-sm
              font-medium
              text-semantic-text-secondary
              bg-semantic-bg-primary
              border border-semantic-border-secondary
              rounded
              hover:bg-semantic-state-active
              transition-colors
            "
            onClick={() => setIsViewModalOpen(false)}
          >
            Close
          </button>
        </div>
      </GenericModal>

      {/* ====================== ALERT: confirm Delete ====================== */}
      {/* <GenericAlert
				isOpen={isAlertOpen}
				onClose={handleCancelDelete}
				onConfirm={confirmDeleteSubnet}
				title="Are you sure?"
				description="This action cannot be undone. This will permanently delete the subnet."
				cancelLabel="Cancel"
				confirmLabel="Delete"
				iconWidthClass="w-20 h-12"
			/> */}

      {/* Overlay spinner */}
      {isFetching && (
        <div
          className="
            fixed
            inset-0
            flex flex-col items-center justify-center
            bg-black/50
            z-[9999]
          "
        >
          <h2 className="text-xl font-semibold mb-4 text-white">
            Processing request...
          </h2>
          <div
            className="
              animate-spin
              rounded-full
              h-12 w-12
              border-4
              border-gray-300
              border-t-blue-500
            "
          />
        </div>
      )}
    </GenericCard>
  );
}

export default PublicSubnetInventoryPage;
